import { useEffect, useMemo, useState } from "react";
import {
  getJson, useJson, nf, ago, fmtDate, card, control, Chip, ConfidenceBadge, entityPath, packagePath, CATEGORY_BY_SLUG, PACKAGE_TYPE, Box,
} from "./shared";
import { StixBar, StixTypeTag } from "./Stix";
import { graphHash } from "../graph/handoff";
import { RelationshipsTab } from "./Profile";
import { RelationshipGraph } from "./Graph";
import { ReportedInTab } from "./Reports";
import { StixExport } from "./StixExport";

// Malicious open-source packages (npm, PyPI, RubyGems, NuGet, crates.io, Go) from OSV,
// as indicators. List: #/kb/packages (a tab of Indicators). Page: #/kb/package/<MAL id>.
// Package names are shown as text and never linked to their registry page.

const PAGE = 50;
// Each registry's own brand colour, for its chip.
const ECO_COLOR = { npm: "#cb3837", pypi: "#3775a9", rubygems: "#e9573f", nuget: "#004880", crates: "#a72145", go: "#00add8" };
const MALICIOUS = "#dc2626";
const SORTS = [["recent", "Newest"], ["modified", "Recently updated"], ["confidence", "Highest confidence"], ["name", "Name"]];

const Mono = ({ children, ui, style }) => <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: ui.fs(12), wordBreak: "break-all", ...style }}>{children}</span>;

export function EcoChip({ eco, name, ui }) {
  const c = ECO_COLOR[eco] || "#71717a";
  return <span style={{ fontSize: ui.fs(10), fontWeight: 800, color: c, background: c + "18", border: `1px solid ${c}55`, borderRadius: 5, padding: "1px 6px", whiteSpace: "nowrap", letterSpacing: 0.3 }}>{name || eco}</span>;
}

function CopyButton({ text, ui, label = "Copy" }) {
  const [done, setDone] = useState(false);
  return (
    <button onClick={() => navigator.clipboard?.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); })} style={{ ...ui.HP.navBtn, padding: "4px 10px", fontSize: ui.fs(11), whiteSpace: "nowrap" }}>{done ? "Copied ✓" : label}</button>
  );
}

const versionText = (p) => (p.allVersions ? "Every version" : p.nVersions ? `${p.versions.slice(0, 4).join(", ")}${p.nVersions > 4 ? ` +${p.nVersions - 4} more` : ""}` : "—");

function NamedChips({ named, ui }) {
  const { getConf } = ui;
  if (!named?.length) return null;
  return named.map(n => (
    <a key={n.id} href={entityPath(n)} title="Named in the record (inferred, Low): it may only compare the package to this" style={{ textDecoration: "none" }}>
      <Chip ui={ui} color={getConf(n.type).color}>{n.name}</Chip>
    </a>
  ));
}

export function PackageRows({ items, ui, compact }) {
  const { B, fs } = ui;
  const cell = { padding: "9px 12px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), verticalAlign: "top" };
  return (
    <div style={{ ...card(ui), overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: compact ? 520 : 760 }}>
        <thead>
          <tr style={{ textAlign: "left", fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>
            <th style={{ padding: "9px 12px", fontWeight: 700 }}>Package</th>
            <th style={{ padding: "9px 12px", fontWeight: 700 }}>What it does</th>
            {!compact && <th style={{ padding: "9px 12px", fontWeight: 700 }}>Malicious versions</th>}
            {!compact && <th style={{ padding: "9px 12px", fontWeight: 700 }}>Reported by</th>}
            <th style={{ padding: "9px 12px", fontWeight: 700, whiteSpace: "nowrap" }}>Published</th>
          </tr>
        </thead>
        <tbody>
          {items.map(p => (
            <tr key={p.id}>
              <td style={{ ...cell, maxWidth: 300 }}>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <EcoChip eco={p.eco} name={p.ecosystem} ui={ui} />
                  <a href={packagePath(p.id)} style={{ color: B.text, textDecoration: "none", fontWeight: 700 }}><Mono ui={ui}>{p.name}</Mono></a>
                </div>
                <div style={{ marginTop: 4, display: "flex", gap: 6, alignItems: "center" }}><span style={{ fontSize: fs(10), color: B.textDim }}>{p.id}</span><ConfidenceBadge confidence={p.confidence} ui={ui} /></div>
              </td>
              <td style={{ ...cell, maxWidth: 420 }}>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {p.tags.map(t => <Chip key={t} ui={ui}>{t}</Chip>)}
                  <NamedChips named={p.named} ui={ui} />
                  {p.nIocs > 0 && <Chip ui={ui} title="File hashes or other indicators in the record">{nf(p.nIocs)} IOC{p.nIocs === 1 ? "" : "s"}</Chip>}
                </div>
                {p.about && <div style={{ marginTop: p.tags.length || p.named.length ? 5 : 0, color: B.textMuted, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.about}</div>}
                {!p.about && !p.tags.length && <span style={{ color: B.textDim }}>The record doesn't say</span>}
              </td>
              {!compact && <td style={{ ...cell, color: p.allVersions ? B.textMuted : B.text }}><Mono ui={ui} style={{ fontSize: fs(11) }}>{versionText(p)}</Mono></td>}
              {!compact && <td style={{ ...cell, color: B.textMuted }}>{p.sources.length ? p.sources.join(", ") : "—"}</td>}
              <td style={{ ...cell, whiteSpace: "nowrap", color: B.textMuted }} title={fmtDate(p.published) || ""}>{ago(p.published)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PackagesState({ st, ui }) {
  const { fs, B } = ui;
  if (!st) return null;
  return (
    <div style={{ fontSize: fs(11), color: B.textDim }}>
      {st.builtAt ? `OSV read ${ago(st.builtAt)} · records reported or updated in the last ${st.days} days` : st.error ? `Unavailable (${st.error})` : "Loading… the package list arrives a few minutes after the server starts"}
      {st.refreshing && st.builtAt ? " · refreshing now" : ""}
    </div>
  );
}

// ─── #/kb/packages ───

export function KbPackageList({ meta, ui, params = {} }) {
  const { B, fs, getConf } = ui;
  const [q, setQ] = useState(params.q || "");
  const [query, setQuery] = useState(params.q || "");
  const [eco, setEco] = useState(params.eco || "");
  const [tag, setTag] = useState(params.tag || "");
  const [source, setSource] = useState(params.source || "");
  const [sort, setSort] = useState(params.sort || "recent");
  const named = params.named || "";
  const [pages, setPages] = useState([]);
  const [state, setState] = useState({ loading: true, error: "", total: 0, counts: null, names: {}, ecoNames: {}, st: null });

  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  const base = useMemo(() => {
    const u = new URLSearchParams({ sort });
    if (query) u.set("q", query);
    if (eco) u.set("eco", eco);
    if (tag) u.set("tag", tag);
    if (source) u.set("source", source);
    if (named) u.set("named", named);
    return `/api/kb/packages?${u}`;
  }, [query, eco, tag, source, sort, named]);
  useEffect(() => {
    let live = true;
    setState(s => ({ ...s, loading: true, error: "" }));
    getJson(`${base}&offset=0&limit=${PAGE}`, { fresh: true })
      .then(d => { if (!live) return; setPages([d.items]); setState({ loading: false, error: "", total: d.total, counts: d.counts, names: d.sourceNames, ecoNames: d.ecosystemNames, st: d.state }); })
      .catch(e => live && setState(s => ({ ...s, loading: false, error: e.message })));
    return () => { live = false; };
  }, [base]);
  const items = pages.flat();
  const more = () => getJson(`${base}&offset=${items.length}&limit=${PAGE}`).then(d => setPages(p => [...p, d.items])).catch(() => {});
  const opts = (m) => Object.entries(m || {}).sort((a, b) => b[1] - a[1]);
  const cat = CATEGORY_BY_SLUG.packages;

  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, margin: "4px 0 6px", flexWrap: "wrap" }}>
        <h2 style={{ fontSize: fs(22), fontWeight: 900, margin: 0, color: B.text }}>Malicious packages</h2>
        <span style={{ fontSize: fs(13), color: getConf(PACKAGE_TYPE).color, fontWeight: 800 }}>{meta?.counts?.packages ? nf(meta.counts.packages) : ""}</span>
        <StixTypeTag ui={ui} type="indicator" />
      </div>
      <p style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 6px", maxWidth: "110ch", lineHeight: 1.6 }}>
        {cat.blurb} Each one is an indicator like an IP address or file hash, with a confidence score from who reported it, and its own page with its relationships, graph and STIX export.
        What a package does is read from its record by keyword rules, and groups or campaigns it names (such as Contagious Interview) are linked as inferred, Low confidence. Part of <a href="#/kb/supply-chain" style={{ color: B.accent }}>Critical Infrastructure › Supply Chain</a>.
      </p>
      <div style={{ margin: "0 0 14px" }}><PackagesState st={state.st || meta?.packages} ui={ui} /></div>
      {named && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, fontSize: fs(12), color: B.textMuted }}>
          Showing the packages whose records name one group, campaign or malware family. <a href="#/kb/packages" style={{ color: B.accent }}>Show all packages</a>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by package name, MAL- or GHSA- id, or words in its description…" aria-label="Filter packages" style={{ ...control(ui), flex: "1 1 260px", minWidth: 0 }} />
        <select value={eco} onChange={e => setEco(e.target.value)} aria-label="Ecosystem" style={control(ui)}>
          <option value="">All ecosystems</option>
          {opts(state.counts?.ecosystems).map(([k, n]) => <option key={k} value={k}>{state.ecoNames?.[k] || k} ({nf(n)})</option>)}
          {eco && !state.counts?.ecosystems?.[eco] && <option value={eco}>{state.ecoNames?.[eco] || eco}</option>}
        </select>
        <select value={tag} onChange={e => setTag(e.target.value)} aria-label="What it does" style={control(ui)}>
          <option value="">Anything it does</option>
          {opts(state.counts?.tags).map(([k, n]) => <option key={k} value={k}>{k} ({nf(n)})</option>)}
          {tag && !state.counts?.tags?.[tag] && <option value={tag}>{tag}</option>}
        </select>
        <select value={source} onChange={e => setSource(e.target.value)} aria-label="Reported by" style={control(ui)}>
          <option value="">Any source</option>
          {opts(state.counts?.sources).map(([k, n]) => <option key={k} value={k}>{state.names?.[k] || k} ({nf(n)})</option>)}
          {source && !state.counts?.sources?.[source] && <option value={source}>{source}</option>}
        </select>
        <select value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort" style={control(ui)}>
          {SORTS.map(([v, l]) => <option key={v} value={v}>Sort: {l}</option>)}
        </select>
      </div>
      <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 10 }}>{state.loading ? "Loading…" : `${nf(state.total)} package${state.total === 1 ? "" : "s"}`}</div>
      {state.error && <div style={{ color: "#dc2626", fontSize: fs(12), marginBottom: 10 }}>{state.error}</div>}
      {!!items.length && <PackageRows items={items} ui={ui} />}
      {items.length < state.total && <button onClick={more} style={{ ...control(ui), cursor: "pointer", marginTop: 14 }}>Show {Math.min(PAGE, state.total - items.length)} more</button>}
    </>
  );
}

// ─── #/kb/package/<MAL id> ───

const HASH_LABEL = { sha1: "SHA-1", sha256: "SHA-256", sha512: "SHA-512", md5: "MD5" };

function HashLines({ hashes, ui }) {
  const { B, fs } = ui;
  return Object.entries(hashes).filter(([, v]) => v).map(([k, v]) => (
    <div key={k} style={{ display: "flex", gap: 8, alignItems: "baseline", minWidth: 0 }}>
      <span style={{ fontSize: fs(10), color: B.textDim, minWidth: 52 }}>{HASH_LABEL[k]}</span><Mono ui={ui} style={{ fontSize: fs(11) }}>{v}</Mono>
    </div>
  ));
}

function IndicatorsBox({ p, ui }) {
  const { B, fs } = ui;
  const io = p.iocs || {};
  const list = (label, values) => values?.length ? (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 4 }}>{label}</div>
      {values.map(v => <div key={v}><Mono ui={ui}>{v}</Mono></div>)}
    </div>
  ) : null;
  if (!p.tarballs.length && !p.nIocs) return null;
  return (
    <Box title="Indicators" ui={ui} right={<span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>shown as text, never linked</span>}>
      {p.tarballs.length > 0 && (
        <div>
          <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 6 }}>Package archives (as downloaded from the registry)</div>
          {p.tarballs.map((t, i) => (
            <div key={i} style={{ padding: "6px 0", borderTop: i ? `1px solid ${B.border}` : "none" }}>
              {t.file && <div style={{ fontSize: fs(12), color: B.text, fontWeight: 600, marginBottom: 2 }}><Mono ui={ui}>{t.file}</Mono></div>}
              <HashLines hashes={{ sha1: t.sha1, sha256: t.sha256, sha512: t.sha512 }} ui={ui} />
            </div>
          ))}
        </div>
      )}
      {list("Domains", io.domains)}
      {list("IP addresses", io.ips)}
      {list("URLs", io.urls)}
      {io.files?.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 4 }}>Files</div>
          {io.files.map((f, i) => (
            <div key={i} style={{ padding: "6px 0", borderTop: i ? `1px solid ${B.border}` : "none" }}>
              {f.paths?.length > 0 && <div style={{ fontSize: fs(12), color: B.text }}><Mono ui={ui}>{f.paths.join(", ")}</Mono></div>}
              {f.note && <div style={{ fontSize: fs(11), color: B.textMuted, margin: "2px 0" }}>{f.note}</div>}
              <HashLines hashes={{ md5: f.md5, sha1: f.sha1, sha256: f.sha256 }} ui={ui} />
            </div>
          ))}
        </div>
      )}
    </Box>
  );
}

export function KbPackage({ id, ui, initialTab }) {
  const { B, fs, TypeIcon, getConf } = ui;
  const { data, error, loading } = useJson(`/api/kb/package/${encodeURIComponent(id)}`);
  const { data: ent } = useJson(data ? `/api/kb/entity/${encodeURIComponent(data.key)}` : null);
  const [tab, setTab] = useState(initialTab || "overview");
  useEffect(() => { setTab(initialTab || "overview"); }, [initialTab, id]);
  if (loading) return <div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div>;
  if (error) return <div style={{ ...card(ui), padding: 20, color: B.textMuted, fontSize: fs(13) }}>{error} <a href="#/kb/packages" style={{ color: B.accent }}>Back to packages</a></div>;
  const color = getConf(PACKAGE_TYPE).color;
  const rels = ent?.relationships || [];
  const subject = ent?.summary || { id: data.key, type: PACKAGE_TYPE, name: data.name, kind: "package", osvId: data.id };
  const tabs = [
    { k: "overview", l: "Overview" },
    { k: "relationships", l: "Relationships", n: rels.length },
    rels.length ? { k: "graph", l: "Graph" } : null,
    ent?.reports?.length ? { k: "reports", l: "Reported in", n: ent.reports.length } : null,
    { k: "stix", l: "STIX 2.1" },
  ].filter(Boolean);
  const tabBtn = (t) => (
    <button key={t.k} onClick={() => setTab(t.k)} aria-current={tab === t.k ? "page" : undefined} style={{ background: "none", border: "none", borderBottom: `2px solid ${tab === t.k ? B.primary : "transparent"}`, color: tab === t.k ? B.text : B.textDim, padding: "9px 14px", cursor: "pointer", fontSize: fs(13), fontWeight: tab === t.k ? 700 : 500, fontFamily: "'Satoshi', sans-serif", whiteSpace: "nowrap" }}>
      {t.l}{t.n ? <span style={{ color: B.textDim, fontWeight: 500, marginLeft: 6 }}>{nf(t.n)}</span> : null}
    </button>
  );
  const linkStyle = { ...ui.HP.navBtn, padding: "4px 10px", fontSize: fs(11), textDecoration: "none" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ fontSize: fs(12), color: B.textDim }}><a href="#/kb/ips" style={{ color: B.textDim, textDecoration: "none" }}>Indicators</a> / <a href="#/kb/packages" style={{ color: B.textDim, textDecoration: "none" }}>Packages</a> / {data.name}</div>

      <div style={{ ...card(ui), borderTop: `3px solid ${MALICIOUS}`, padding: "20px 22px" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><TypeIcon type={PACKAGE_TYPE} color={color} size={22} /></div>
          <div style={{ flex: "1 1 320px", minWidth: 0 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: fs(11), color: MALICIOUS, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.2 }}>Malicious {data.ecosystem} package · indicator</span>
              <EcoChip eco={data.eco} name={data.ecosystem} ui={ui} />
              {data.tags.map(t => <Chip key={t} ui={ui}>{t}</Chip>)}
            </div>
            <h1 style={{ fontSize: fs(20), fontWeight: 800, margin: "6px 0 8px", color: B.text, minWidth: 0 }}><Mono ui={ui} style={{ fontSize: fs(20) }}>{data.name}</Mono></h1>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <ConfidenceBadge confidence={data.confidence} ui={ui} size="lg" />
              <NamedChips named={data.named} ui={ui} />
              <CopyButton text={data.name} ui={ui} label="Copy name" />
              <a href={data.osvUrl} target="_blank" rel="noreferrer noopener" style={linkStyle}>OSV {data.id} ↗</a>
              {data.aliases.map(a => <a key={a} href={`https://github.com/advisories/${a}`} target="_blank" rel="noreferrer noopener" style={linkStyle}>GitHub {a} ↗</a>)}
            </div>
          </div>
          <a href={graphHash(data.key)} title="See this package and what it's linked to as an interactive graph" style={{ ...ui.HP.primaryBtn, padding: "8px 14px", textDecoration: "none", fontSize: fs(12) }}>View in Graph</a>
        </div>
        <StixBar apiKey={data.key} ui={ui} />
      </div>

      <div role="tablist" style={{ display: "flex", gap: 2, borderBottom: `1px solid ${B.border}`, overflowX: "auto" }}>{tabs.map(tabBtn)}</div>

      {tab === "overview" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))", gap: 14, alignItems: "start" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
            <Box title="What it does" ui={ui}>
              {data.description || data.about
                ? <div style={{ fontSize: fs(13), color: B.text, lineHeight: 1.65, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{data.description || data.about}</div>
                : <div style={{ fontSize: fs(13), color: B.textMuted }}>The record only says the package contains malicious code.</div>}
              {data.perSource.filter(s => s.text !== data.description).map((s, i) => (
                <div key={i} style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${B.border}` }}>
                  <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 4 }}>{s.source} says</div>
                  <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{s.text}</div>
                </div>
              ))}
            </Box>
            <IndicatorsBox p={data} ui={ui} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
            <Box title="Malicious versions" ui={ui}>
              {data.allVersions
                ? <div style={{ fontSize: fs(13), color: B.text }}>Every version. The package was malicious from the start, so remove it entirely.</div>
                : <>
                  <div style={{ fontSize: fs(12), color: B.textMuted, marginBottom: 6 }}>Only these versions: the package may be a legitimate one whose maintainer account or build was hijacked.</div>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>{data.versions.map(v => <Chip key={v} ui={ui} color={MALICIOUS}><Mono ui={ui} style={{ fontSize: fs(11) }}>{v}</Mono></Chip>)}</div>
                </>}
              {data.purl && <div style={{ marginTop: 10, fontSize: fs(11), color: B.textDim }}>Package URL: <Mono ui={ui} style={{ fontSize: fs(11), color: B.textMuted }}>{data.purl}</Mono></div>}
            </Box>
            <Box title="Reported by" ui={ui}>
              {data.sources.length
                ? <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>{data.sources.map(s => <Chip key={s} ui={ui}>{s}</Chip>)}</div>
                : <div style={{ fontSize: fs(12), color: B.textMuted }}>Added to OpenSSF's database directly.</div>}
              {data.finders.length > 0 && <div style={{ marginTop: 8, fontSize: fs(12), color: B.textMuted }}>Credited: {data.finders.join(", ")}</div>}
              <div style={{ marginTop: 8, fontSize: fs(11), color: B.textDim }}>Published {fmtDate(data.published)} · updated {ago(data.modified)}</div>
            </Box>
            {data.refs.length > 0 && (
              <Box title="References" ui={ui}>
                {data.refs.map(u => <div key={u} style={{ fontSize: fs(12), marginBottom: 4, wordBreak: "break-all" }}><a href={u} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{u}</a></div>)}
              </Box>
            )}
            {data.reports.length > 0 && (
              <Box title="In our reports" ui={ui}>
                {data.reports.map(r => <div key={r.id} style={{ fontSize: fs(12), marginBottom: 6 }}><a href={`#/kb/e/${encodeURIComponent(r.id)}`} style={{ color: B.text, fontWeight: 600 }}>{r.title}</a> <span style={{ color: B.textDim }}>· {r.source}, {fmtDate(r.published)}</span></div>)}
              </Box>
            )}
          </div>
        </div>
      )}
      {tab === "relationships" && (rels.length
        ? <div style={{ ...card(ui), overflow: "hidden" }}><RelationshipsTab relationships={rels} subject={data.name} ui={ui} /></div>
        : <div style={{ ...card(ui), padding: 18, fontSize: fs(13), color: B.textMuted }}>{ent ? "No relationships yet." : "Loading…"}</div>)}
      {tab === "graph" && <div style={{ ...card(ui), padding: 14 }}><RelationshipGraph subject={subject} relationships={rels} ui={ui} onShowBucket={() => setTab("relationships")} /></div>}
      {tab === "reports" && <ReportedInTab reports={ent?.reports || []} subject={subject} ui={ui} />}
      {tab === "stix" && (ent?.entity ? <StixExport id={data.key} entity={ent.entity} ui={ui} /> : <div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div>)}
    </div>
  );
}
